import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { 
  EtLogo, 
  TrinityLogo 
} from "@/assets/img/imageAssets";

export default function EtHeader() {

  // First Created - 15 Aug 2025
  // Author - Atul Pandey
  // Updated - 03 Oct 2025 for variant support
  // Updated - 26 Oct 2025 for logo and other changes
  // Updated - 19 Nov 2025 - cleaned up link breaks
  // Updated - 20 Nov 2025 - added links for /investors


  // --------------------------------------------------
  // 1. Auto-detect variant based on URL
  // --------------------------------------------------
  const { pathname } = useLocation();

  let variant = "eternity"; // default for '/', '/clients', '/investors'
  
  if (pathname.startsWith("/trinity/platform")) {
  variant = "platform";
  } 
  else if (pathname.startsWith("/trinity/meet")) {
    variant = "trinity";
  } 
  else if (pathname.startsWith("/clients")) {
    variant = "clients";
  }
  else if (pathname.startsWith("/investors")) {
    variant = "investors";   // ✅ NEW
  }
  else {
    variant = "eternity";
  }


  // --------------------------------------------------
  // 2. Navigation link sets (variant-based)
  // --------------------------------------------------

  const navLinksEternity = [
    { name: "About", href: "/about" },
    { name: "Mission", href: "/#mission" },
    { name: "Approach", href: "/#approach" },
    { name: "Voices", href: "/#success" },
    { name: "ThinkWare", href: "/thinkware" },   // ✅ 21 Nov 2025
    { name: "Trinity", href: "/trinity/platform" },
    { name: "Investors", href: "/investors" },
  ];

  const navLinksTrinityImmersive = [
    { name: "Platform", href: "/trinity/platform" },
    { name: "CxOs", href: "/clients" },
    // { name: "ThinkWare", href: "/thinkware" },   // ✅ 21 Nov 2025
    { name: "Investors", href: "/investors" },
    { name: "Eternity", href: "/" },
  ];

  const navLinksTrinityPlatform = [
    { name: "About", href: "/trinity/platform#overview" },
    { name: "Why?", href: "/trinity/platform#problems" },
    { name: "How It Works", href: "/trinity/platform#how-it-works" },
    { name: "CxOs", href: "/clients" },
    // { name: "ThinkWare", href: "/thinkware" },   // ✅ 21 Nov 2025
    { name: "Investors", href: "/investors" },
    { name: "Eternity", href: "/" },
  ];

  const navLinksClients = [
    { name: "CxOs", href: "/clients#clients-hero" },
    { name: "Who We Serve?", href: "/clients#segments" },
    { name: "Where Are You?", href: "/clients#growth-states" },
    { name: "Mind Frames", href: "/clients#inner-states" },
    { name: "Voices", href: "/clients#success" },
    { name: "ThinkWare", href: "/thinkware" },   // ✅ 21 Nov 2025
    { name: "Trinity", href: "/trinity/platform" },
    { name: "Investors", href: "/investors" },
  ];

  // 20 Nov 2025
  const navLinksInvestors = [
    // { name: "Home", href: "/investors#investors-hero" },
    // { name: "How We Help", href: "/investors#how-we-help" },
    // { name: "Patterns", href: "/investors#patterns" },
    // { name: "Voices", href: "/investors#success" },
    { name: "CxOs", href: "/clients" },
    { name: "Trinity", href: "/trinity/platform" },
    { name: "Eternity", href: "/" },
    
  ];


  // Pick final nav links
  const navLinks =
  variant === "platform"
    ? navLinksTrinityPlatform
    : variant === "trinity"
    ? navLinksTrinityImmersive
    : variant === "clients"
    ? navLinksClients               
    : variant === "investors"     // 20 Nov 2025
    ? navLinksInvestors           // 20 Nov 2025
    : navLinksEternity;


  // --------------------------------------------------
  // 3. Brand label + logo + home href
  // --------------------------------------------------
  
  const brandText =
  variant === "platform" || variant === "trinity"
    ? "trinity"
    : "eternity";   // includes clients

    const brandHref =
  variant === "trinity"
    ? "/trinity/meet"
    : variant === "platform"
    ? "/trinity/platform"
    : "/";       // eternity + clients


  const brandLogo =
  variant === "platform" || variant === "trinity"
    ? TrinityLogo
    : EtLogo;   // eternity + clients


  // --------------------------------------------------
  // 4. CTA logic (only eternity + platform)
  // --------------------------------------------------
  const cta =
    variant === "platform"
      ? { label: "Experience", href: "/trinity/platform#experience" }
      : variant === "investors"
      ? { label: "Let’s Talk", href: "#investors-cta" }
      : { label: "Let’s Talk", href: "/#contact" };

  // --------------------------------------------------
  // 5. Component UI
  // --------------------------------------------------
  const [isOpen, setIsOpen] = useState(false);

  return (
    <header className="fixed top-0 w-full z-50 bg-white/5 backdrop-blur-lg shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Brand */}
          <a href={brandHref} className="flex items-center cursor-pointer">
            <img 
              src={brandLogo}
              alt={brandText}
              className="h-7 w-auto"
            />
            <motion.span
              className="text-2xl font-bold text-orange-600 ml-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {brandText}
            </motion.span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex space-x-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-gray-300 hover:text-orange-600 transition font-extralight"
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA (not shown on immersive Trinity story) */}
          {variant !== "trinity" && (
            <div className="hidden md:block">
              <a
                href={cta.href}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg shadow hover:bg-orange-700 transition"
              >
                {cta.label}
              </a>
            </div>
          )}

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="p-2">
              {isOpen ? (
                <XMarkIcon className="h-6 w-6 text-gray-700" />
              ) : (
                <Bars3Icon className="h-6 w-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <motion.div
          className="md:hidden bg-white shadow-lg"
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
        >
          <div className="flex flex-col space-y-2 px-4 py-3">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-gray-700 hover:text-orange-600 transition"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </a>
            ))}
            {variant !== "trinity" && (
              <a
                href={cta.href}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg shadow hover:bg-orange-700 transition"
                onClick={() => setIsOpen(false)}
              >
                {cta.label}
              </a>
            )}
          </div>
        </motion.div>
      )}
    </header>
  );
}
